import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingNav from "../components/MarketingNav";
import { Button } from "../components/ui/button";
import { CalendarCheck, Users, Bell, CreditCard, MessageSquare, BarChart3, Check, ArrowRight, Flame } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1706629505300-168aa1604912?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const FEAT1 = "https://customer-assets-m6fa6gv7.emergentagent.net/job_hair-client-hub-212/artifacts/yr8934ns_34edcbac107eff5dc9cafa27ac80a34b.jpg";

const FEATURES = [
  { icon: CalendarCheck, title: "Smart scheduling", desc: "Live availability by provider and service. No double-bookings, ever." },
  { icon: Users, title: "Client CRM", desc: "Profiles, history and client intake captured automatically at booking." },
  { icon: Bell, title: "Reminders & follow-ups", desc: "Automated email and SMS before and after every appointment." },
  { icon: CreditCard, title: "Deposits", desc: "Collect a card deposit to secure bookings and cut no-shows." },
  { icon: MessageSquare, title: "Public booking page", desc: "A shareable link your clients use to book 24/7 — branded to your business." },
  { icon: BarChart3, title: "Operational dashboard", desc: "Today's chairs, revenue and upcoming visits at a glance." },
];

const TIERS = [
  { id: "starter", name: "Starter", price: 9, yearly: 90, was: 29, wasYearly: 290, popular: false, features: ["Up to 3 providers", "Unlimited appointments", "Email reminders & follow-ups", "Client management", "Public booking page"] },
  { id: "professional", name: "Professional", price: 19, yearly: 190, was: 79, wasYearly: 790, popular: true, features: ["Up to 10 providers", "Everything in Starter", "SMS reminders (Twilio)", "Client deposits", "Waitlist & receptionist accounts"] },
  { id: "premium", name: "Premium", price: 39, yearly: 390, was: 149, wasYearly: 1490, popular: false, features: ["Unlimited providers", "Everything in Professional", "Priority support", "Advanced analytics"] },
];

export default function Marketing() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="label-eyebrow text-accent">Simple, efficient, management</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mt-4">
                Run your salon on autopilot.
              </h1>
              <p className="text-lg text-muted-foreground mt-5 max-w-lg leading-relaxed">
                Booking Babe books clients, fills your calendar, collects deposits and sends the reminders — so you can focus on your clients.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md h-12 px-7" data-testid="hero-signup" onClick={() => navigate("/signup")}>
                  Start 14-day free trial <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-md h-12 px-7" data-testid="hero-pricing" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>
                  See pricing
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-4">No credit card required. Cancel anytime.</p>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
            <img src={HERO} alt="Salon owner managing bookings on a laptop" className="rounded-xl border border-border w-full object-cover max-h-[80vh]" />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <span className="label-eyebrow text-accent">Everything in one place</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">The full front desk, minus the front desk.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-[#F5EFE6] border border-accent/30 rounded-lg p-6 hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-200 text-neutral-900" data-testid={`feature-${f.title}`}>
                <div className="w-10 h-10 rounded-md bg-accent/15 text-accent flex items-center justify-center mb-4"><Icon size={20} aria-hidden="true" /></div>
                <h3 className="font-semibold text-lg text-neutral-900">{f.title}</h3>
                <p className="text-neutral-600 text-sm mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Showcase */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <img src={FEAT1} alt="Beauty professional at work" className="rounded-xl border border-border w-full object-cover max-h-[80vh] grayscale" />
          <div>
            <span className="label-eyebrow text-accent">Your own booking link</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Share one link. Fill your calendar.</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Every business gets a branded booking page at <code className="text-foreground bg-background px-1.5 py-0.5 rounded border border-border">bookingbabe.app/book/your-salon</code>. Clients pick a service, a provider, a time, answer a quick intake and pay a deposit — all before they arrive.
            </p>
            <ul className="mt-6 space-y-3">
              {["Real-time availability", "Quick client intake at booking", "Optional card deposit to secure the slot", "Instant confirmation email + SMS"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm"><span className="w-5 h-5 rounded-full bg-success/15 text-[hsl(var(--success))] flex items-center justify-center"><Check size={13} /></span>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="label-eyebrow text-primary">Pricing</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Simple monthly plans.</h2>
          <p className="text-muted-foreground mt-3">Start with a 14-day free trial. Upgrade when you're ready.</p>
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-1.5 rounded-full bg-accent/15 border border-accent/40 text-accent text-sm font-medium" data-testid="launch-pricing-banner">
            <Flame size={15} /> Limited launch pricing — lock in these rates before they rise
          </div>
          <div className="inline-flex items-center gap-1 mt-6 p-1 rounded-full border border-border bg-card">
            <button data-testid="period-month" onClick={() => setPeriod("month")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${period === "month" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Monthly</button>
            <button data-testid="period-year" onClick={() => setPeriod("year")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${period === "year" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Yearly <span className="text-[hsl(var(--success))] font-medium">· 2 months free</span></button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12 items-start">
          {TIERS.map((t) => (
            <div key={t.id} data-testid={`pricing-${t.id}`}
              className={`rounded-xl border p-7 bg-card relative ${t.popular ? "border-primary shadow-lg md:-translate-y-2" : "border-border"}`}>
              {t.popular && <span className="absolute -top-3 left-7 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">Most popular</span>}
              <span className="absolute -top-3 right-7 inline-flex items-center gap-1 bg-[hsl(var(--success))] text-white text-xs font-semibold px-3 py-1 rounded-full" data-testid={`launch-ribbon-${t.id}`}><Flame size={12} /> Launch price</span>
              <h3 className="font-display text-xl font-bold">{t.name}</h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-display text-4xl font-extrabold">${period === "year" ? t.yearly : t.price}</span>
                <span className="text-muted-foreground text-sm">/{period === "year" ? "year" : "month"}</span>
                <span className="text-muted-foreground/70 text-base line-through decoration-2" data-testid={`was-price-${t.id}`}>${period === "year" ? t.wasYearly : t.was}</span>
              </div>
              {period === "year" && <div className="text-xs text-[hsl(var(--success))] mt-1">${(t.price * 12 - t.yearly)} saved vs monthly</div>}
              <Button className={`w-full mt-5 rounded-md ${t.popular ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`} data-testid={`pricing-cta-${t.id}`} onClick={() => navigate("/signup")}>
                Start free trial
              </Button>
              <ul className="mt-6 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm"><Check size={16} className="text-primary mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-primary-foreground">Ready to fill your calendar?</h2>
          <p className="text-primary-foreground/70 mt-3">Set up your salon in under two minutes.</p>
          <Button size="lg" className="mt-7 bg-accent text-accent-foreground hover:bg-accent/90 rounded-md h-12 px-8" data-testid="cta-signup" onClick={() => navigate("/signup")}>
            Create your free account
          </Button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <img src="/bookingbabe-logo.png" alt="Booking Babe" className="w-6 h-6 rounded object-cover" />
            <span className="font-display font-bold text-foreground">Booking Babe</span>
          </div>
          <div>© {new Date().getFullYear()} Booking Babe. Built for beauty businesses — hair, nails, skin, spa & more.</div>
        </div>
      </footer>
    </div>
  );
}
